import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import { updateClaimQueueStatus } from '../../../../store/participantService';
import Button from '../../../formElements/Button';
import SingleSelect from '../../../formElements/SingleSelect';
import TextAreaField from '../../../formElements/TextAreaField';

import { NOTE_MAX_CHAR, validate } from './validateStatusUpdateModal';

import form from '../../../../commonStyles/formStyles.module.css';
import modal from '../../../../commonStyles/modal.module.css';

const UPDATE_STATUS_MESSAGE = 'Claim queue status has been updated';

const StatusUpdateModal = ({ isOpen, setOpen, claimStatus, toStatusesIds }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [newEntry, setNewEntry] = useState({
        id: '',
        notes: '',
        statusId: ''
    });
    const [errors, setErrors] = useState({});

    // STORE STATE
    const loadingClaimQueues = useSelector(
        (state) => state.entities.participantService.loadingClaimQueues
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        setNewEntry({
            id: claimStatus?.id,
            notes: '',
            statusId: ''
        });
    }, [isOpen]);

    useEffect(() => {
        if (successMessage === UPDATE_STATUS_MESSAGE) onClose();
    }, [successMessage]);

    // EVENT HANDLERS
    const onClose = () => {
        setErrors({});
        setOpen(false);
    };

    const clearError = (key) => {
        // eslint-disable-next-line no-unused-vars
        setErrors((prev) => (({ [key]: _, ...rest }) => rest)(prev));
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateClaimQueueStatus(newEntry, UPDATE_STATUS_MESSAGE));
    };

    // RENDER
    return (
        <Dialog open={isOpen} fullWidth={true}>
            <div className={modal.modalHeader}>
                <DialogTitle>Update Status</DialogTitle>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="status_update_form">
                    <SingleSelect
                        id={'statusId'}
                        disabled={loadingClaimQueues}
                        label="Set Claim Status"
                        placeholder="Select claim status..."
                        mandatory={true}
                        menuItems={toStatusesIds}
                        selectedId={newEntry.statusId}
                        selected={toStatusesIds.find((el) => el.id === newEntry.statusId)}
                        error={errors.statusId}
                        onChange={(chosenId) => onUpdate('statusId', chosenId)}
                    />

                    <TextAreaField
                        id="notes"
                        disabled={loadingClaimQueues}
                        label="Notes"
                        placeholder="Notes"
                        maxLength={NOTE_MAX_CHAR}
                        rows={8}
                        value={newEntry.notes}
                        count={`${newEntry.notes.length}/${NOTE_MAX_CHAR}`}
                        error={errors.notes}
                        onChange={(e) => onUpdate('notes', e.target.value)}
                    />

                    <div className={modal.actionButtons}>
                        <Button
                            id="statusUpdateSubmitButton"
                            disabled={loadingClaimQueues || Object.keys(errors).length}
                            content="Update"
                            onClick={onSubmit}
                        />

                        <Button
                            id="statusUpdateCancelButton"
                            disabled={loadingClaimQueues}
                            type="button"
                            content="Cancel"
                            onClick={onClose}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

StatusUpdateModal.propTypes = {
    isOpen: PropTypes.bool,
    setOpen: PropTypes.func,
    claimStatus: PropTypes.object,
    toStatusesIds: PropTypes.array
};

export default StatusUpdateModal;
